import { Box, IconButton, Typography } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { MdCheck, MdClose, MdOutlineRemoveRedEye } from 'react-icons/md';
import { WebhookLog } from '@/apis/agency/webhooks/types';

interface ColumnsProps {
  onView: (id: number) => void;
}

function isSuccessStatus(log: WebhookLog) {
  if (log.http_status != null) return log.http_status >= 200 && log.http_status < 300;
  return log.status === 'success' || log.status === 'processed';
}

function StatusBadge({ log }: { log: WebhookLog }) {
  const success = isSuccessStatus(log);
  const code = log.http_status ?? (success ? 'OK' : 'ERR');

  return (
    <Box
      display='inline-flex'
      alignItems='center'
      gap={0.4}
      px={1.25}
      py={0.4}
      borderRadius='999px'
      sx={{
        bgcolor: success ? 'success.light' : 'error.light',
        color: success ? 'success.main' : 'error.main'
      }}
    >
      {success ? <MdCheck size={14} /> : <MdClose size={14} />}
      <Typography fontSize={12} fontWeight={500} lineHeight={1.2}>
        {code}
      </Typography>
    </Box>
  );
}

function truncateEvent(event: string | null | undefined, max = 16) {
  if (!event) return '—';
  if (event.length <= max) return event;
  return `${event.slice(0, max)}...`;
}

export function getWebhookLogColumns({ onView }: ColumnsProps): ColumnDef<WebhookLog>[] {
  return [
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge log={row.original} />
    },
    {
      id: 'webhook_name',
      header: 'Webhook',
      cell: ({ row }) => (
        <Typography fontSize={13} fontWeight={500} color='text.primary'>
          {row.original.webhook?.name || '—'}
        </Typography>
      )
    },
    {
      accessorKey: 'event_type',
      header: 'Event',
      cell: ({ row }) => (
        <Typography fontSize={13} color='text.primary' title={row.original.event_type || undefined}>
          {truncateEvent(row.original.event_type)}
        </Typography>
      )
    },
    {
      accessorKey: 'attempt',
      header: 'Attempts',
      cell: ({ row }) => (
        <Typography fontSize={13} color='text.primary'>
          {row.original.attempt ?? '—'}
        </Typography>
      )
    },
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => (
        <IconButton size='medium' sx={{ color: '#777777' }} onClick={() => onView(row.original.id)}>
          <MdOutlineRemoveRedEye />
        </IconButton>
      )
    }
  ];
}
